/**
 * ResourceContextMenu - Generic context menu for sidebar resource items
 *
 * Provides standard actions for all resource types:
 * - Open in split view
 * - Open in new tab
 * - Additional custom actions per resource type
 */

import React, { useRef, useEffect, useCallback } from "react";
import { FaColumns, FaPlus, FaExternalLinkAlt } from "react-icons/fa";
import { ContextMenuItem, ContextMenuDivider } from "./context-menu-helpers";
import { MotionDiv } from "./motion-types";
import { Z_INDEX } from "@/utils/z-index";
import { useSplitView } from "@/components/layout/split-view";
import { DragPayload } from "@/utils/drag-types";

export interface ResourceContextMenuProps {
  /** Position of the context menu */
  position: { x: number; y: number };
  /** Drag payload for the resource (used for split view) */
  dragPayload: DragPayload;
  /** Callback to close the menu */
  onClose: () => void;
  /** Additional menu items to render before the divider */
  additionalItems?: React.ReactNode;
  /** Additional menu items to render after the standard items */
  additionalItemsAfter?: React.ReactNode;
}

const ResourceContextMenu: React.FC<ResourceContextMenuProps> = ({
  position,
  dragPayload,
  onClose,
  additionalItems,
  additionalItemsAfter,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { isActive: isSplitViewActive, panels, openWithCurrentRoute, isInSplitView } = useSplitView();

  // Check if this resource is already in the split view
  const isResourceInSplitView = isInSplitView(dragPayload.type, dragPayload.id);
  const canAddToSplit = !isResourceInSplitView;

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 240),
    y: Math.min(position.y, window.innerHeight - 200),
  };

  // Handle opening in split view
  const handleOpenInSplit = useCallback(() => {
    openWithCurrentRoute(dragPayload);
    onClose();
  }, [openWithCurrentRoute, dragPayload, onClose]);

  // Handle opening in new browser tab
  const handleOpenInNewTab = useCallback(() => {
    if (dragPayload.route) {
      window.open(dragPayload.route, "_blank");
    }
    onClose();
  }, [dragPayload.route, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <MotionDiv
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="fixed bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1 min-w-[220px]"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x, zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU }}
    >
      {/* Additional items before standard actions */}
      {additionalItems}

      {additionalItems && <ContextMenuDivider />}

      {/* Open in split view */}
      {canAddToSplit && (
        <ContextMenuItem onClick={handleOpenInSplit}>
          {isSplitViewActive ? (
            <>
              <FaPlus className="text-emerald-400" /> Ajouter au split view ({panels.length} panels)
            </>
          ) : (
            <>
              <FaColumns className="text-indigo-400" /> Ouvrir en split view
            </>
          )}
        </ContextMenuItem>
      )}

      {/* Open in new tab */}
      <ContextMenuItem onClick={handleOpenInNewTab}>
        <FaExternalLinkAlt className="text-blue-400" /> Ouvrir dans un nouvel onglet
      </ContextMenuItem>

      {/* Additional items after standard actions */}
      {additionalItemsAfter && (
        <>
          <ContextMenuDivider />
          {additionalItemsAfter}
        </>
      )}
    </MotionDiv>
  );
};

ResourceContextMenu.displayName = "ResourceContextMenu";

export default ResourceContextMenu;
